
import { computed, defineComponent, reactive, ref, watchEffect } from "vue";
import ModelUploader from "@/components/ModelUploader.vue";
import NextStepButton from "@/components/NextStepButton.vue";
import ConnectorView from "@/components/ConnectorView.vue";
import DimensionsView from "@/components/DimensionsView.vue";
import Delivery, { DeliveryOptions } from "@/components/Delivery.vue";
import DeliveryPaymentView from "@/components/DeliveryPaymentView.vue";

enum WizardSteps {
  UPLOAD,
  DIMENSIONS,
  CONNECTORS,
  DELIVERY,
  CHECKOUT,
}

interface StepT {
  name: WizardSteps;
  isDisabled: boolean;
  label: string;
  title: string;
}

export default defineComponent({
  name: "ModelGeneratorWizard",
  components: {
    NextStepButton,
    ModelUploader,
    DimensionsView,
    ConnectorView,
    Delivery,
    DeliveryPaymentView
  },
  props: {},
  setup() {
    const steps = reactive<StepT[]>([
      {
        name: WizardSteps.UPLOAD,
        isDisabled: false,
        label: "Upload Model",
        title: "Upload Low Poly 3D model here (.blend or .stl)",
      },
      {
        name: WizardSteps.DIMENSIONS,
        isDisabled: false,
        label: "Enter Dimensions",
        title: "Confirm Dimensions",
      },
      {
        name: WizardSteps.CONNECTORS,
        isDisabled: false,
        label: "Choose Connectors",
        title: "Choose type of Connectors",
      },
      {
        name: WizardSteps.DELIVERY,
        isDisabled: false,
        label: "Choose Delivery",
        title: "Delivery Details",
      },
      {
        name: WizardSteps.CHECKOUT,
        isDisabled: false,
        label: "Checkout",
        title: "Checkout",
      },
    ]);

    const activeStep = ref<number>(0);
    const currentStep = computed(() => steps[activeStep.value]);
    const nextStep = computed(() => steps[activeStep.value + 1] || null);

    /**
     * state management for loading + disabled inputs
     */
    const isLoading = ref<boolean>(false);
    const disabledClasses = computed(() => ({
      "cursor-not-allowed": isLoading.value,
      "opacity-30": isLoading.value,
    }));

    /**
     * helpers for manipulating steps
     */
    const getStep = (step: number) => steps[step];
    const setStep = (step: number) => (activeStep.value = step);
    const enableStep = (step: number) => (steps[step].isDisabled = false);
    const disableStep = (step: number) => (steps[step].isDisabled = true);

    const getStepOrder = (step: WizardSteps) => step + 1;

    /**
     * handle state for file upload step
     * and validate dimensions
     */
    const modelFile = ref<File | null>(null);
    const handleModelUpload = async (files: FileList) => {
      console.log(files);
      modelFile.value = files[0];
      isLoading.value = true;
      await new Promise((r) => setTimeout(r, 5000));
      isLoading.value = false;
    };
    const toCheckout = (deliveryOption: DeliveryOptions) => {
      isDIY.value = deliveryOption === DeliveryOptions.DIY;
      setStep(activeStep.value + 1);
    };
    const isDIY = ref(false);

    const longestDimension = ref<number>(0);
    const unitOptions = ref(["ft", "in", "m", "mm", "cm"]);
    const longestDimensionUnit = ref("ft");
    const isLongestDimensionValid = computed(() => longestDimension.value > 0);

    /**
     * prevent user from entering dimensions
     * greater than or less than specified value
     */
    watchEffect(() => {
      const min = 1;
      const max = 100;
      if (longestDimension.value < min) {
        longestDimension.value = min;
      } else if (longestDimension.value > max) {
        longestDimension.value = max;
      }
    });

    /**
     * handle state for connector step
     */
    const connectorInfo = reactive({
      connectors: 0,
      edges: 0,
    });

    const handleConnectorInput = (selection: string) => console.log(selection);

    return {
      WizardSteps,
      steps,
      activeStep,
      currentStep,
      nextStep,
      isLoading,
      disabledClasses,
      modelFile,
      connectorInfo,
      longestDimension,
      unitOptions,
      longestDimensionUnit,
      isLongestDimensionValid,
      getStepOrder,
      getStep,
      setStep,
      enableStep,
      disableStep,
      handleModelUpload,
      handleConnectorInput,
      toCheckout,
      isDIY,
    };
  },
});
